/**
 * Factions — docs/05-content-plan.md §5.
 *
 * Six factions in three opposed pairs. Reputation is zero-sum *by construction*:
 * a pair shares one signed axis and each faction reads it through its pole, so
 * rep(a) + rep(opposed(a)) === 0 always, even at the clamps. Storing two
 * independent numbers and subtracting from one when adding to the other quietly
 * stops being zero-sum the moment either hits a clamp — this doesn't.
 *
 * Shop availability and hostility are derived from reputation, never stored, so
 * there is exactly one source of truth for "whose side am I on".
 */

export type FactionId =
  | 'sealed_choir'
  | 'emberwrights'
  | 'grafted'
  | 'static_order'
  | 'hollow_pilgrims'
  | 'marrow_court';

/** One axis per opposed pair. Positive pole vs negative pole. */
export type AxisId = 'veil' | 'substrate' | 'continuance';

export interface FactionDef {
  id: FactionId;
  name: string;
  eraOrigin: string;
  interest: string;
  /** What they sell / grant. Plain string ids — combat/ owns the real defs. */
  gives: readonly string[];
  /** What they want done in the world. */
  wants: string;
  axis: AxisId;
  /** +1 or -1: which end of the shared axis this faction occupies. */
  pole: 1 | -1;
  /** Shop id the world/ui layer resolves; listed here so gating is one lookup. */
  shopId: string;
}

export const FACTIONS: readonly FactionDef[] = [
  {
    id: 'sealed_choir',
    name: 'The Sealed Choir',
    eraOrigin: 'occult/ancient',
    interest: 'containment of void',
    gives: ['module:radiant', 'module:resonant', 'ward-charms', 'consecration-flasks'],
    wants: 'void materials destroyed',
    axis: 'veil',
    pole: 1,
    shopId: 'shop.choir-reliquary',
  },
  {
    id: 'hollow_pilgrims',
    name: 'Hollow Pilgrims',
    eraOrigin: 'cosmic',
    interest: 'dissolution',
    gives: ['module:void', 'module:temporal', 'erasure-sigils', 'drift-cloaks'],
    wants: 'shrines desecrated',
    axis: 'veil',
    pole: -1,
    shopId: 'shop.pilgrim-procession',
  },
  {
    id: 'emberwrights',
    name: 'Emberwrights',
    eraOrigin: 'industry',
    interest: 'extraction',
    gives: ['module:kinetic', 'module:pyre', 'drills', 'blast-charges'],
    wants: 'biomes burned',
    axis: 'substrate',
    pole: 1,
    shopId: 'shop.emberwright-foundry',
  },
  {
    id: 'grafted',
    name: 'The Grafted',
    eraOrigin: 'biotech',
    interest: 'mutation',
    gives: ['body-modification', 'module:fungal', 'module:blood', 'symbiote-cultures'],
    wants: 'you carry their spores',
    axis: 'substrate',
    pole: -1,
    shopId: 'shop.grafted-hollow',
  },
  {
    id: 'static_order',
    name: 'Static Order',
    eraOrigin: 'cyber',
    interest: 'preservation of machines',
    gives: ['module:nanite', 'module:galvanic', 'repair-cradles', 'drone-frames'],
    wants: 'machines repaired',
    axis: 'continuance',
    pole: 1,
    shopId: 'shop.static-cache',
  },
  {
    id: 'marrow_court',
    name: 'The Marrow Court',
    eraOrigin: 'flesh/myth',
    interest: 'blood debt',
    gives: ['module:blood', 'module:resonant', 'marrow-brands', 'debt-contracts'],
    wants: 'blood offerings',
    axis: 'continuance',
    pole: -1,
    shopId: 'shop.marrow-court',
  },
];

export const FACTION_IDS: readonly FactionId[] = FACTIONS.map((f) => f.id);

export const FACTION_BY_ID: Readonly<Record<FactionId, FactionDef>> = Object.fromEntries(
  FACTIONS.map((f) => [f.id, f]),
) as Record<FactionId, FactionDef>;

export const OPPOSED_PAIRS: readonly (readonly [FactionId, FactionId])[] = [
  ['sealed_choir', 'hollow_pilgrims'],
  ['emberwrights', 'grafted'],
  ['static_order', 'marrow_court'],
];

const OPPOSED: Readonly<Record<FactionId, FactionId>> = (() => {
  const m: Partial<Record<FactionId, FactionId>> = {};
  for (const [a, b] of OPPOSED_PAIRS) {
    m[a] = b;
    m[b] = a;
  }
  return m as Record<FactionId, FactionId>;
})();

export function opposedOf(id: FactionId): FactionId {
  return OPPOSED[id];
}

export function getFaction(id: FactionId): FactionDef {
  const f = FACTION_BY_ID[id];
  if (!f) throw new Error(`unknown faction: ${id}`);
  return f;
}

// ------------------------------------------------------------------ deeds

/**
 * Player behaviours that move reputation. Named as world events so world/ and
 * combat/ can fire them by string without importing anything from meta/.
 */
export type DeedId =
  | 'void-material-destroyed'
  | 'ward-restored'
  | 'shrine-desecrated'
  | 'relic-dissolved'
  | 'biome-burned'
  | 'vein-drilled'
  | 'spores-carried'
  | 'graft-accepted'
  | 'machine-repaired'
  | 'nanite-preserved'
  | 'blood-offered'
  | 'champion-slain'
  // One per faction, for the deed the world can always observe: you killed
  // somebody who belonged to them. These carry *negative* weight against their
  // own faction, which is what makes them the mirror of the twelve above —
  // `adjust` then hands the opposite pole the same magnitude for free, so
  // butchering Emberwrights is literally the same act as courting the Grafted.
  | 'choir-slain'
  | 'pilgrim-slain'
  | 'emberwright-slain'
  | 'grafted-slain'
  | 'static-slain'
  | 'marrow-slain';

export interface DeedDef {
  id: DeedId;
  faction: FactionId;
  weight: number;
  name: string;
}

export const DEEDS: Readonly<Record<DeedId, DeedDef>> = {
  'void-material-destroyed': {
    id: 'void-material-destroyed',
    faction: 'sealed_choir',
    weight: 3,
    name: 'Void matter annihilated',
  },
  'ward-restored': { id: 'ward-restored', faction: 'sealed_choir', weight: 8, name: 'Ward restored' },
  'shrine-desecrated': {
    id: 'shrine-desecrated',
    faction: 'hollow_pilgrims',
    weight: 9,
    name: 'Shrine desecrated',
  },
  'relic-dissolved': {
    id: 'relic-dissolved',
    faction: 'hollow_pilgrims',
    weight: 5,
    name: 'Relic dissolved',
  },
  'biome-burned': { id: 'biome-burned', faction: 'emberwrights', weight: 10, name: 'Biome burned' },
  'vein-drilled': { id: 'vein-drilled', faction: 'emberwrights', weight: 4, name: 'Vein drilled out' },
  'spores-carried': { id: 'spores-carried', faction: 'grafted', weight: 4, name: 'Spores carried' },
  'graft-accepted': { id: 'graft-accepted', faction: 'grafted', weight: 12, name: 'Graft accepted' },
  'machine-repaired': {
    id: 'machine-repaired',
    faction: 'static_order',
    weight: 6,
    name: 'Machine repaired',
  },
  'nanite-preserved': {
    id: 'nanite-preserved',
    faction: 'static_order',
    weight: 3,
    name: 'Nanite bloom preserved',
  },
  'blood-offered': { id: 'blood-offered', faction: 'marrow_court', weight: 7, name: 'Blood offered' },
  'champion-slain': {
    id: 'champion-slain',
    faction: 'marrow_court',
    weight: 11,
    name: 'Champion slain in the Court name',
  },

  // Weight -1 is not timidity, it is the only value that makes the axis read
  // as a *balance* rather than a countdown. Every enemy on the substrate axis
  // is tagged to one pole or the other, so a run that fights whatever it meets
  // ends near zero and a run that hunts one side deliberately walks off the
  // end: 15 bodies closes their shop (SHOP_CLOSES_AT), 40 makes them hostile.
  // A larger weight would pin all three axes inside the first band, and a
  // reputation that is always ±100 is not a decision anybody made.
  'choir-slain': { id: 'choir-slain', faction: 'sealed_choir', weight: -1, name: 'Choir servant slain' },
  'pilgrim-slain': { id: 'pilgrim-slain', faction: 'hollow_pilgrims', weight: -1, name: 'Pilgrim slain' },
  'emberwright-slain': {
    id: 'emberwright-slain',
    faction: 'emberwrights',
    weight: -1,
    name: 'Emberwright slain',
  },
  'grafted-slain': { id: 'grafted-slain', faction: 'grafted', weight: -1, name: 'Grafted slain' },
  'static-slain': { id: 'static-slain', faction: 'static_order', weight: -1, name: 'Order machine broken' },
  'marrow-slain': { id: 'marrow-slain', faction: 'marrow_court', weight: -1, name: 'Court blood spilled' },
};

/** The "you killed one of ours" deed, per faction. */
export const SLAIN_DEED: Readonly<Record<FactionId, DeedId>> = {
  sealed_choir: 'choir-slain',
  hollow_pilgrims: 'pilgrim-slain',
  emberwrights: 'emberwright-slain',
  grafted: 'grafted-slain',
  static_order: 'static-slain',
  marrow_court: 'marrow-slain',
};

/** Mirrors `entity/enemyData`'s rank, without importing it — meta/ imports nothing. */
export type KillRank = 'base' | 'elite' | 'boss';

/**
 * How much louder a rank's death is.
 *
 * Scaled on the *weight*, never on the deed count: a boss is one body, and a
 * codex line reading "Court blood spilled ×8" for a single kill would be the
 * game lying to the player about what it watched them do.
 */
export const KILL_WEIGHT_BY_RANK: Readonly<Record<KillRank, number>> = { base: 1, elite: 3, boss: 8 };

// ------------------------------------------------------------- thresholds

export const REP_MAX = 100;

/** Standing bands. Ordered high to low; first match wins. */
export type Standing = 'exalted' | 'allied' | 'friendly' | 'neutral' | 'wary' | 'hostile' | 'nemesis';

const STANDING_BANDS: readonly (readonly [number, Standing])[] = [
  [75, 'exalted'],
  [45, 'allied'],
  [15, 'friendly'],
  [-15, 'neutral'],
  [-40, 'wary'],
  [-75, 'hostile'],
];

/** Below this the faction stops trading with you — the "serving one closes the other". */
export const SHOP_CLOSES_AT = -15;
/** Below this their patrols spawn hostile instead of ambient. */
export const HOSTILE_AT = -40;
/** Offer tiers gate the good stock behind actual commitment. */
export const OFFER_TIER_2_AT = 45;
export const OFFER_TIER_3_AT = 75;

export function standingOf(rep: number): Standing {
  for (const [min, name] of STANDING_BANDS) if (rep >= min) return name;
  return 'nemesis';
}

// ------------------------------------------------------------------ state

/** Serialisable. Three numbers is the entire faction save footprint. */
export interface FactionState {
  axes: Record<AxisId, number>;
  /** Deed counts, kept for UI ("why do they hate me") and for save round-trip. */
  deeds: Partial<Record<DeedId, number>>;
}

export function createFactionState(): FactionState {
  return { axes: { veil: 0, substrate: 0, continuance: 0 }, deeds: {} };
}

const AXIS_IDS: readonly AxisId[] = ['veil', 'substrate', 'continuance'];

/**
 * Repair a faction blob read off disk.
 *
 * Reputation is `axes[axis] * pole`, and a missing axis makes that `NaN` —
 * which clamps to nothing, compares `false` against every standing band, and so
 * reads as permanent neutrality with no error anywhere. That is the same class
 * of silent zero docs/13 §2 catalogues five times over, so the shape is
 * rebuilt rather than trusted. Never throws: a save that fails to load costs
 * the player their profile, and defaulting is always the better answer.
 */
export function normaliseFactionState(raw: unknown): FactionState {
  const src = (raw ?? {}) as Partial<FactionState>;
  const out = createFactionState();
  const axes = (src.axes ?? {}) as Partial<Record<AxisId, unknown>>;
  for (const a of AXIS_IDS) {
    const n = Number(axes[a]);
    out.axes[a] = Number.isFinite(n) ? clamp(n, -REP_MAX, REP_MAX) : 0;
  }
  if (src.deeds && typeof src.deeds === 'object') {
    for (const [k, v] of Object.entries(src.deeds)) {
      if (!DEEDS[k as DeedId]) continue;
      const n = Math.floor(Number(v));
      if (Number.isFinite(n) && n > 0) out.deeds[k as DeedId] = n;
    }
  }
  return out;
}

export interface FactionView {
  id: FactionId;
  name: string;
  reputation: number;
  standing: Standing;
  shopOpen: boolean;
  shopId: string;
  /** 0 = closed, 1..3 = stock depth. */
  offerTier: 0 | 1 | 2 | 3;
  offers: readonly string[];
  hostile: boolean;
  /** 0..1 — fraction of this faction's encounters that spawn hostile. */
  hostileEncounterRate: number;
}

/**
 * One faction's movement from one act, as the event ticker wants to say it.
 *
 * `banded` is the field that matters for legibility: reputation ticking by one
 * is noise, and crossing from *wary* into *hostile* is the moment the world
 * changes. docs/13 §6 point 5 asks for fixtures that assert the player was
 * *told* — this is the value they assert on.
 */
export interface RepChange {
  faction: FactionId;
  name: string;
  delta: number;
  reputation: number;
  standing: Standing;
  /** True when this change moved the faction into a different standing band. */
  banded: boolean;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * A view bound to a FactionState by reference — run state owns the data, this
 * owns the rules. Constructing one is free, so callers may do it per-frame.
 */
export class Factions {
  constructor(public readonly state: FactionState = createFactionState()) {}

  reputation(id: FactionId): number {
    const f = getFaction(id);
    return this.state.axes[f.axis] * f.pole;
  }

  /**
   * Move `id` by `delta`. The opposed faction moves by exactly `-delta` because
   * they read the same axis from the other pole — no second write, no drift.
   */
  adjust(id: FactionId, delta: number): number {
    const f = getFaction(id);
    const axis = f.axis;
    this.state.axes[axis] = clamp(this.state.axes[axis] + delta * f.pole, -REP_MAX, REP_MAX);
    return this.reputation(id);
  }

  /**
   * Record a world deed. Returns the acting faction's new reputation.
   *
   * `weightScale` multiplies the reputation moved without multiplying the count
   * recorded, which is the difference between "you killed a boss" and "you
   * killed eight things". Defaults to 1, so every existing caller is unchanged.
   */
  applyDeed(deed: DeedId, times = 1, weightScale = 1): number {
    const d = DEEDS[deed];
    if (!d) throw new Error(`unknown deed: ${deed}`);
    this.state.deeds[deed] = (this.state.deeds[deed] ?? 0) + times;
    return this.adjust(d.faction, d.weight * times * weightScale);
  }

  /**
   * The deed the world can always observe: you killed somebody who belonged to
   * them (issue #11). `EnemyDef.factions` tags all 54 enemies, so this needs no
   * new authoring — it needs a call site, which is the whole reason this file
   * sat dead.
   *
   * Both sides of every touched axis are reported, not just the one you hit.
   * "The Emberwrights will remember this" without "and the Grafted are pleased"
   * is half a mechanic: the opposed movement *is* the design, and a player who
   * never sees it happen has no reason to believe it does.
   *
   * Unknown or empty tags are ignored rather than thrown on. This runs on every
   * enemy death in the frame loop; an id typo must cost a reputation point, not
   * the run.
   */
  slay(factions: readonly FactionId[], rank: KillRank = 'base'): RepChange[] {
    const mul = KILL_WEIGHT_BY_RANK[rank] ?? KILL_WEIGHT_BY_RANK.base;
    const touched = new Set<FactionId>();
    for (const id of factions) {
      if (!FACTION_BY_ID[id]) continue;
      touched.add(id);
      touched.add(opposedOf(id));
    }
    if (touched.size === 0) return [];

    const before = new Map<FactionId, { rep: number; standing: Standing }>();
    for (const id of touched) before.set(id, { rep: this.reputation(id), standing: this.standing(id) });

    for (const id of factions) {
      if (!FACTION_BY_ID[id]) continue;
      this.applyDeed(SLAIN_DEED[id], 1, mul);
    }

    const out: RepChange[] = [];
    for (const id of touched) {
      const was = before.get(id)!;
      const rep = this.reputation(id);
      if (rep === was.rep) continue;
      const standing = this.standing(id);
      out.push({
        faction: id,
        name: getFaction(id).name,
        delta: rep - was.rep,
        reputation: rep,
        standing,
        banded: standing !== was.standing,
      });
    }
    return out;
  }

  standing(id: FactionId): Standing {
    return standingOf(this.reputation(id));
  }

  /** Serving the opposite number is what closes this one's doors. */
  shopOpen(id: FactionId): boolean {
    return this.reputation(id) > SHOP_CLOSES_AT;
  }

  offerTier(id: FactionId): 0 | 1 | 2 | 3 {
    const r = this.reputation(id);
    if (r <= SHOP_CLOSES_AT) return 0;
    if (r >= OFFER_TIER_3_AT) return 3;
    if (r >= OFFER_TIER_2_AT) return 2;
    return 1;
  }

  /** Stock actually purchasable right now, widening with standing. */
  offers(id: FactionId): readonly string[] {
    const tier = this.offerTier(id);
    if (tier === 0) return [];
    const gives = getFaction(id).gives;
    // Tier 1 shows the first half, tier 2 all but the signature item, tier 3 all.
    if (tier === 3) return gives;
    if (tier === 2) return gives.slice(0, Math.max(1, gives.length - 1));
    return gives.slice(0, Math.max(1, Math.ceil(gives.length / 2)));
  }

  hostile(id: FactionId): boolean {
    return this.reputation(id) <= HOSTILE_AT;
  }

  /** Ramps from 0 at HOSTILE_AT to 1 at -REP_MAX. */
  hostileEncounterRate(id: FactionId): number {
    const r = this.reputation(id);
    if (r > HOSTILE_AT) return 0;
    return clamp((HOSTILE_AT - r) / (REP_MAX + HOSTILE_AT), 0, 1);
  }

  view(id: FactionId): FactionView {
    const f = getFaction(id);
    return {
      id,
      name: f.name,
      reputation: this.reputation(id),
      standing: this.standing(id),
      shopOpen: this.shopOpen(id),
      shopId: f.shopId,
      offerTier: this.offerTier(id),
      offers: this.offers(id),
      hostile: this.hostile(id),
      hostileEncounterRate: this.hostileEncounterRate(id),
    };
  }

  /** Full derived snapshot — what the UI and world spawner read. */
  derived(): Record<FactionId, FactionView> {
    const out: Partial<Record<FactionId, FactionView>> = {};
    for (const id of FACTION_IDS) out[id] = this.view(id);
    return out as Record<FactionId, FactionView>;
  }

  /** Shops the player can currently walk into. */
  openShops(): FactionId[] {
    return FACTION_IDS.filter((id) => this.shopOpen(id));
  }

  /** Factions whose encounters are now hostile. */
  hostileFactions(): FactionId[] {
    return FACTION_IDS.filter((id) => this.hostile(id));
  }

  reset(): void {
    this.state.axes.veil = 0;
    this.state.axes.substrate = 0;
    this.state.axes.continuance = 0;
    this.state.deeds = {};
  }
}

/**
 * The zero-sum invariant: every opposed pair's reputations sum to zero.
 *
 * **Test-facing** — `tests/factions.test.ts` asserts it at eight points, across
 * deeds, decay, a hand-corrupted state and a save round-trip, which is the only
 * way to check a property that has to hold *after every mutation* rather than
 * at one call site. `tests/liveness.test.ts` excuses it under
 * `TEST_FACING_EXPORTS` for that reason.
 *
 * It used to claim it was "cheap enough to assert in dev builds", and no build
 * asserted it — #118 removed the clause rather than the function. An invariant
 * a suite genuinely enforces is worth keeping; a claim about a build that does
 * not exist is what makes an export look alive when it is not.
 */
export function isZeroSum(state: FactionState): boolean {
  const f = new Factions(state);
  return OPPOSED_PAIRS.every(([a, b]) => f.reputation(a) + f.reputation(b) === 0);
}

/**
 * NO VIEW MODEL LIVES HERE. `Factions.derived()` is the primitive the UI layer
 * builds its panel shape from (`ui/viewModels.ts` owns `FactionsVM`), and
 * `FACTIONS` and `DEEDS` carry the copy — `wants`, `gives`, and each deed's
 * name and weight. A second builder in meta/ would be a competing source of
 * truth for what a standing *means*, which is the failure docs/13 §2 catalogues
 * five times over.
 */
